import React from 'react';
import { trackLandingCTA, trackPricingPlanClicked } from '@/lib/analytics';
import { Reveal } from '../components/Reveal';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  type TierKey,
  type BillingInterval,
  CAPITAL_FEE_ANNUAL_BPS,
  tierPrice,
} from '@/billing/tiers';

interface Tier {
  /** Canonical billing tier key. Routes signup to the right plan. */
  tierKey: TierKey;
  name: string;
  badge: string;
  description: string;
  features: string[];
  /** False for a plan the page shows but nobody can buy yet (Managed). */
  open: boolean;
}

/**
 * Marketing ruling M1 (2026-10-20), Phase A: the page showed Observer ($0,
 * open) and Managed ($99/mo + 0.20%/yr of live capital under mandate, "not
 * available yet"); since P0-09 (see `landingTiers` below) it renders Observer
 * only. Professional, Team and Enterprise are HIDDEN here, not
 * deleted: `TIER_KEYS`, `TIER_FEATURES`, `FLAT_MONTHLY_PRICE` and
 * `CAPITAL_FEE_ANNUAL_BPS` in `@/billing/tiers` still carry them because the
 * capital-fee billing plumbing (and the backend `tier_gate`) references them.
 * Deleting the tiers from billing is the owner's call (M1 "What needs you").
 *
 * `HIDDEN_TIER_KEYS` exists so `PricingParity.test.tsx` can keep proving
 * that every canonical tier is either advertised or deliberately hidden --
 * never silently dropped or invented.
 */
export const HIDDEN_TIER_KEYS: readonly TierKey[] = ['professional', 'team', 'enterprise'];

const managedCapitalFeePct = (CAPITAL_FEE_ANNUAL_BPS.managed / 100).toFixed(2);

/** Exported for CI parity test against `TIER_KEYS`. */
export const pricingTiers: Tier[] = [
  {
    tierKey: 'observer',
    name: 'Observer',
    badge: 'Open now',
    description: 'A paper account, free. The full loop before any money moves.',
    features: [
      'One paper broker connection (Alpaca paper)',
      'The full loop: mandate, proposal, approval, paper run',
      'The library and your mandate',
      'Community support',
    ],
    open: true,
  },
  {
    tierKey: 'managed',
    name: 'Managed',
    badge: 'Not available yet',
    description: 'Not available until live trading opens. Leave your email and we will tell you when.',
    features: [
      'Everything in Observer',
      'Live trading of admitted rules, once open',
      `The ${managedCapitalFeePct}% is charged only on live capital, never on paper`,
      'Email support',
    ],
    open: false,
  },
];

/**
 * Implementation plan P0-09 (2026-10-22, R71 M5 "now", R72 T3: no prices for
 * a product that does not exist yet): the public page renders ONLY the plans
 * someone can actually use today. Managed is no longer shown on the landing
 * or /pricing page -- no price, no capital fee, no waitlist -- because live
 * trading does not exist and the manager product it would price is still in
 * development. It stays in `pricingTiers` (and the billing registry) because
 * the signed-in billing page (`BillingSettings`) reads its words from there;
 * hiding it in-app is a separate change.
 */
export const landingTiers: Tier[] = pricingTiers.filter((t) => t.open);

export const Pricing: React.FC<{ onSignUp?: (tier?: string, interval?: BillingInterval) => void }> = ({ onSignUp }) => {
  // Only the free paper plan is shown, so there is no billing interval to choose.
  const interval: BillingInterval = 'monthly';

  return (
    <section id="pricing" className="py-28 px-6 scroll-mt-20">
      <div className="max-w-[900px] mx-auto">
        <Reveal
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm text-[#2dd4a0] font-medium uppercase tracking-widest mb-4">
            Pricing
          </p>
          <h2 className="font-heading text-3xl md:text-[2.75rem] font-bold text-white leading-tight">
            Free while we build.{' '}
            <span className="text-gradient">No paid plan today.</span>
          </h2>
          <p className="text-white/50 text-lg mt-4 max-w-[620px] mx-auto leading-relaxed">
            Mendl Labs is in development. A paper account is free and uses no real
            money. There is nothing to buy yet.
          </p>
        </Reveal>

        <div className="grid gap-6 items-stretch max-w-[440px] mx-auto">
          {landingTiers.map((tier, i) => {
            // Observer is "$0", full stop (not "$0 forever").
            const price = tierPrice(tier.tierKey, interval);

            return (
            <Reveal
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative flex flex-col p-8 rounded-2xl transition-all duration-300 ${
                tier.open
                  ? 'glow-border glow-primary bg-white/[0.03]'
                  : 'border border-white/[0.06] bg-white/[0.02] hover:border-white/10'
              }`}
            >
              <div
                className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                  tier.open
                    ? 'border border-[#2dd4a0]/40 bg-[#2dd4a0]/10 text-[#2dd4a0]'
                    : 'border border-amber-400/30 bg-amber-400/10 text-amber-300'
                }`}
              >
                {tier.badge}
              </div>

              <h3 className="font-heading text-xl font-semibold text-white mb-1">
                {tier.name}
              </h3>
              <p className="text-white/50 text-sm mb-6">{tier.description}</p>

              <div className="mb-8">
                <span className="text-4xl font-heading font-bold text-white">{price}</span>
              </div>

              <ul className="flex-1 space-y-3 mb-8">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/50 text-sm">
                    <Check size={16} className="text-[#2dd4a0] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => { trackPricingPlanClicked(tier.tierKey); trackLandingCTA('pricing'); onSignUp?.(tier.tierKey, interval); }}
                className="w-full glow-primary"
              >
                Open a paper account
              </Button>
            </Reveal>
            );
          })}
        </div>

        <p className="text-center text-white/40 text-sm mt-10">
          No other plans today.
        </p>
      </div>
    </section>
  );
};
